import axios, { AxiosError } from 'axios';
import { DIGISAKA_API_ORIGIN } from '../config/digisakaApiOrigin';
import { notifyComputeUsageFromAxiosHeaders } from '../utils/inferenceComputeBudget';

type AuthAwareRequestConfig = {
  _retry?: boolean;
  /** When true, a 401 does not trigger /auth/refresh (avoids stale-cookie errors right after login). */
  _skipAuthRefresh?: boolean;
  url?: string;
};

/**
 * Authenticated JSON client for Digisaka FastAPI `/api`.
 * JWTs are sent via httpOnly cookies (`credentials: 'include'`).
 */

export { DIGISAKA_API_ORIGIN } from '../config/digisakaApiOrigin';

export const API_BASE_URL = `${DIGISAKA_API_ORIGIN}/api`;

/** Dispatched when refresh fails — AuthProvider clears React session state. */
export const AUTH_SESSION_INVALIDATED_EVENT = 'digisaka:auth-session-invalidated';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

type RefreshSubscriber = {
  resolve: () => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

const subscribeTokenRefresh = (resolve: () => void, reject: (error: unknown) => void) => {
  refreshSubscribers.push({ resolve, reject });
};

const resolveRefreshSubscribers = () => {
  refreshSubscribers.forEach(({ resolve }) => resolve());
  refreshSubscribers = [];
};

const rejectRefreshSubscribers = (error: unknown) => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/auth/refresh') ||
    url.includes('/auth/login') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/guest')
  );
}

/** Clear legacy tokens from older builds (httpOnly cookies replace these). */
export function clearLegacyAuthStorage(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

export function notifyAuthSessionInvalidated(): void {
  clearLegacyAuthStorage();
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('user');
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_INVALIDATED_EVENT));
  }
}

apiClient.interceptors.response.use(
  (response) => {
    notifyComputeUsageFromAxiosHeaders(response.headers);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AuthAwareRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (originalRequest._skipAuthRefresh || isAuthEndpoint(originalRequest.url)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            () => resolve(apiClient(originalRequest)),
            reject
          );
        });
      }

      isRefreshing = true;

      try {
        await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
        resolveRefreshSubscribers();
        return apiClient(originalRequest);
      } catch (refreshError) {
        rejectRefreshSubscribers(refreshError);
        notifyAuthSessionInvalidated();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
